/**
 * Inline interstitial shown when a user has a valid session but no
 * effective access — covers three shapes:
 *
 *   1. `lockedOut` from /api/auth/me (session valid, users row absent)
 *   2. role === null (post-session-delete defensive case)
 *   3. admin-only page accessed by a viewer
 *
 * Rendered inside the existing app shell — NOT a separate route — so
 * the user keeps the topbar (email + sign-out) and isn't blackholed.
 */
export type LockedOutKind = 'no_access' | 'admin_only' | 'module_denied';

interface Props {
  kind: LockedOutKind;
  email?: string;
  moduleKey?: string;
}

const TITLE: Record<LockedOutKind, string> = {
  no_access: 'No access',
  admin_only: 'Admins only',
  module_denied: 'Not authorized',
};

function bodyFor(kind: LockedOutKind, moduleKey?: string): string {
  if (kind === 'no_access') {
    return 'You are signed in, but your account does not have dashboard access yet. Ask an administrator to invite you.';
  }
  if (kind === 'admin_only') {
    return 'This area is restricted to administrators.';
  }
  return `You do not have access to the ${moduleKey ?? 'requested'} area. Ask an administrator to enable it.`;
}

export default function LockedOutCard({ kind, email, moduleKey }: Props) {
  return (
    <div className="card" style={{ maxWidth: 560, margin: '40px auto' }}>
      <h2 style={{ marginTop: 0 }}>{TITLE[kind]}</h2>
      <p className="subtle" style={{ marginTop: 8 }}>
        {bodyFor(kind, moduleKey)}
      </p>
      {email && (
        <p className="subtle" style={{ marginTop: 16, fontSize: 13 }}>
          Signed in as <code>{email}</code>
        </p>
      )}
    </div>
  );
}
